<template>
  <page-view>
    <template #header>
      <base-header
        class="mb-5"
        :isNew="true"
        :has-intro="false"
        :show-beta="true"
        :title="'Tutor'"
      >
        <template #hasHeaderWarning>
          <BaseAlert
            class="mx-2 mb-5"
            v-if="noCredits"
            type="warning"
            :title="$t('message.pages.pandaAssist.headerWarning.title')"
            :description="
              $t('message.pages.pandaAssist.headerWarning.description')
            "
          >
            <base-btn @click="openCredits" size="small" type="secondary">
              {{
                $t('message.pages.pandaAssist.headerWarning.button')
              }}</base-btn
            >
          </BaseAlert>
        </template>
        <template #actions>
          <base-btn @click="openModal" type="primary" :disabled="!hasEditVideoPerm">
            <span class="responsive-text">
              {{ $t('message.pages.pandaAssist.createPandaAssist') }}
            </span>
          </base-btn>
        </template>
      </base-header>
    </template>
    <template #content>
      <v-card
        v-if="fetching"
        class="d-flex flex-row justify-center align-center"
        height="100%"
        width="100%"
        flat
      >
        <loading-logo class="mb-16" :value="300" />
      </v-card>
      <div
        v-else-if="data.length === 0"
        class="align-center justify-center"
        style="height: 100%; display: grid"
      >
        <v-row
          class="d-flex flex-column"
          no-gutters
          justify="center"
          align="center"
        >
          <img
            class="emptyStateTitle mb-4"
            src="@/assets/videosEmptyState.svg"
            max-width="240"
          />
          <span class="body-regular-large textType3--text">
            {{ $t('message.pages.pandaAssist.emptyState.title') }}</span
          >
          <span class="body-regular-small textType6--text">
            {{ $t('message.pages.pandaAssist.emptyState.description') }}
          </span>
          <base-btn class="mt-5" size="small" @click="openModal">
            <span>
              {{ $t('message.pages.pandaAssist.emptyState.buttonText') }}
            </span>
          </base-btn>
        </v-row>
      </div>
      <div v-else>
        <div class="d-flex justify-space-between">
          <v-input
            v-if="!isMobileBreakpoint"
            v-model="searchQuery"
            style="max-width: 370px"
            prepend-inner-icon="panda-search"
            size="small"
            ref="autoCompleteInput"
            :placeholder="'Buscar por chats'"
            class="ml-2 mb-1 custom-icon-size"
            hide-details
            dense
            solo
            flat
          />

          <span class="body-regular-smaller textType6--text pt-2 mr-3">
            {{ data.length }} {{ $t('message.pages.pandaAssist.items') }}
          </span>
        </div>
        <v-card
          flat
          width="100%"
          class="data-table__header d-flex justify-space-between"
          ref="dataTableHeader"
        >
          <div>
            <v-card
              class="d-flex align-center pl-2"
              height="45px"
              flat
              width="96px"
            >
              <v-row no-gutters>
                <v-card class="d-flex align-center" height="48px" flat>
                  <div class="d-flex align-center">
                    <span class="caption pr-1">
                      {{ $t('message.pages.pandaAssist.name') }}
                    </span>
                  </div>
                </v-card>
              </v-row>
            </v-card>
          </div>
          <div class="d-flex">
            <v-card
              class="d-flex align-center"
              height="45px"
              flat
              width="120px"
            >
              <v-row no-gutters>
                <v-card class="d-flex align-center" height="48px" flat>
                  <div class="d-flex align-center">
                    <span class="caption pr-1">
                      {{ $t('message.pages.pandaAssist.answers') }}
                    </span>
                  </div>
                </v-card>
              </v-row>
            </v-card>

            <v-card
              v-if="!isMobileBreakpoint"
              class="d-flex align-center"
              height="45px"
              flat
              width="405px"
            >
              <v-row no-gutters>
                <v-card class="d-flex align-center" height="48px" flat>
                  <div class="d-flex align-center">
                    <span class="caption pr-1">
                      {{ $t('message.pages.pandaAssist.videos') }}
                    </span>
                  </div>
                </v-card>
              </v-row>
            </v-card>
          </div>
        </v-card>
        <vue-virtual-table
          ref="virtualTable"
          class="data-table__content"
          :class="{ virtualTable: $vuetify.theme.dark }"
          :data="filteredData"
          :config="tableConfig"
          :showHeader="false"
          :height="tableHeight - 200"
          :itemHeight="48"
          :minWidth="320"
        >
          <template slot-scope="scope" slot="titleCommon">
            <v-row
              no-gutters
              class="data-index"
              style="padding: 10px 8px"
              align="center"
              :data-index="scope.index"
              @click="handleEditAgent(scope.row)"
            >
              {{ scope.row.name }}
            </v-row>
          </template>
          <template slot-scope="scope" slot="answersCommon">
            <v-row
              no-gutters
              :style="{ width: '98px' }"
              style="padding: 10px 8px"
              :justify="isMobileBreakpoint ? 'start' : 'end'"
              class="data-index"
              align="center"
              @click="handleEditAgent(scope.row)"
              :data-index="scope.index"
            >
              {{ scope.row.answers_count }}
            </v-row>
          </template>
          <template slot-scope="scope" slot="videosCommon">
            <v-row
              :style="{ width: '98px' }"
              style="padding: 10px 8px"
              no-gutters
              justify="end"
              class="data-index"
              align="center"
              :data-index="scope.index"
            >
              {{ scope.row.videos.length }}
            </v-row>
          </template>
          <template slot-scope="scope" slot="statusCommon">
            <v-row
              :style="{ width: '98px' }"
              style="padding: 10px 8px"
              no-gutters
              class="data-index"
              align="center"
              @click="handleEditAgent(scope.row)"
              :data-index="scope.index"
            >
              <span
                v-if="
                  scope.row.status === 'queued' ||
                  scope.row.status === 'processing'
                "
                class="d-flex"
              >
                <div id="nalp" class="nalps mr-2"></div>

                <v-tooltip
                  bottom
                  persistent
                  :close-delay="2000"
                  :value="scope.row.showTooltip || scope.row.hoverTooltip"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-tag
                      v-bind="attrs"
                      v-on="on"
                      @mouseenter="toggleHover(scope.row, true)"
                      @mouseleave="toggleHover(scope.row, false)"
                      type="default"
                    >
                      {{
                        scope.row.updating
                          ? $t('message.pages.pandaAssist.updating')
                          : $t('message.pages.pandaAssist.converting')
                      }}
                    </v-tag>
                  </template>

                  <span
                    class="d-flex"
                    style="pointer-events: all"
                    :style="
                      !scope.row.showTooltip
                        ? { width: '300px' }
                        : { width: '200px' }
                    "
                  >
                    <span style="width: 190px">
                      {{
                        scope.row.hoverTooltip
                          ? $t('message.pages.pandaAssist.hoverTooltipMessage')
                          : scope.row.showTooltip
                          ? $t('message.pages.pandaAssist.fixedTooltipMessage')
                          : $t('message.pages.pandaAssist.hoverTooltipMessage')
                      }}
                    </span>
                    <base-btn
                      v-if="!scope.row.showTooltip"
                      @click="goToEmbedArticle"
                      type="neutral"
                      size="xsmall"
                      style="width: 110px; align-self: center"
                    >
                      {{ $t('message.embedModal.knowMore') }}
                      <v-icon size="16" class="ml-1">mdi-open-in-new</v-icon>
                    </base-btn>
                  </span>
                </v-tooltip>
              </span>
              <v-tag v-else :type="getTagType(scope.row.status)">
                {{ getStatusText(scope.row.status) }}
              </v-tag>
            </v-row>
          </template>
          <template slot-scope="scope" slot="actionCommon">
            <v-row
              no-gutters
              justify="center"
              class="data-index d-flex"
              align="center"
              :data-index="scope.index"
            >
              <base-btn
                :disabled="scope.row.status === 'failed'"
                class="mr-5 editButton"
                type="tertiary"
                size="small"
                @click="handleEditAgent(scope.row)"
              >
                {{ $t('message.pages.pandaAssist.edit') }}
              </base-btn>
              <v-icon size="16" @click="openMenu(scope.row, $event)">
                panda-menu-action
              </v-icon>
            </v-row>
            <v-menu
              min-width="100px"
              ref="menuRef"
              absolute
              :position-x="menuX"
              :position-y="menuY"
              v-model="menuVisible"
              offset-y
              :close-on-content-click="true"
              v-if="menuItem && menuItem.id === scope.row.id"
            >
              <v-list>
                <v-list-item
                  :disabled="scope.row.status === 'failed' || !hasEditVideoPerm"
                  @click="toggleStatus(scope.row)"
                >
                  <v-list-item-title class="body-regular-smaller">
                    {{
                      scope.row.status === 'disabled'
                        ? $t('message.pages.pandaAssist.active')
                        : $t('message.pages.pandaAssist.disabled')
                    }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="renameItem(scope.row)" :disabled="!hasEditVideoPerm">
                  <v-list-item-title class="body-regular-smaller">{{
                    $t('message.pages.pandaAssist.rename')
                  }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="removeItem(scope.row)" :disabled="!hasEditVideoPerm">
                  <v-list-item-title class="body-regular-smaller removeText">{{
                    $t('message.pages.pandaAssist.remove')
                  }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </vue-virtual-table>
      </div>
      <CreatePandaAssist
        :dialog-visible="dialogVisible"
        @update:dialog-visible="dialogVisible = $event"
      />
    </template>
  </page-view>
</template>

<script>
import VueVirtualTable from 'vue-virtual-table'
import PageView from '@/layout/PageViewV2.vue'
import CreatePandaAssist from './CreatePandaAssist.vue'
import i18n from '@/translations/lang'
import BaseAlert from '@/components/BaseAlert.vue'
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import pandaAssist from '@/services/pandaAssist'
import LoadingLogo from '@/components/LoadingLogo.vue'

export default {
  name: 'PandaAssistPage',
  components: {
    BaseHeader: () => import('@/components/BaseHeaderV2.vue'),
    BaseAlert,
    PageView,
    LoadingLogo,
    VueVirtualTable,
    CreatePandaAssist,
  },
  data() {
    return {
      dialogVisible: false,
      searchQuery: '',
      menuVisible: false,
      showTooltip: true,
      hoverTooltip: false,
      articles: {
        pt: 'https://help.pandavideo.com/pt-br/article/pandaassist-ai-como-criar-chats-com-ia-nos-seus-videos-zdy32s/',
        en: 'https://help.pandavideo.com/en-us/article/pandaassist-ai-how-to-create-ai-chats-in-your-videos-r4624o/',
        es: 'https://help.pandavideo.com/es/article/pandaassist-ai-como-crear-chats-con-ia-en-tus-videos-1ewhd32/',
      },
      menuItem: null,
      menuX: 0,
      tableHeight: window.innerHeight,
      menuY: 0,
      noCredits: false,
      columns: [
        { title: 'Name', key: 'name' },
        { title: 'Status', key: 'status' },
      ],
      data: [],
      fetching: true,
      pandaAssistVideos: [],
      options: {
        rowKey: 'id',
      },
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateTableHeight)
    this.getAssistants()
    this.$root.$on('PandaAssistUpdate', this.refreshList)
    this.$root.$on('goToPandaAssist', this.initializeTooltips())
    localStorage.setItem('hasViewdPandaAssistAI', 'true')

    this.intervalId = setInterval(() => {
      this.updateCreditsBalance()

      const hasPendingStatus = this.data.some(
        (item) => item.status === 'queued' || item.status === 'processing'
      )

      if (hasPendingStatus) {
        this.getAssistants()
      }
    }, 10000)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
    this.$root.$off('PandaAssistUpdate', this.refreshList)

    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  },
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    ...mapGetters('user', ['hasEditVideoPerm']), 
    locale() {
      return this.$i18next.resolvedLanguage
    },
    embedArticle() {
      switch (this.locale) {
        case 'pt-BR':
        case 'pt':
          return this.articles.pt
        case 'es':
          return this.articles.es
        case 'en':
        default:
          return this.articles.en
      }
    },
    filteredData() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) {
        return this.data
      }
      return this.data.filter((item) => item.name.toLowerCase().includes(query))
    },
    tableConfig() {
      const tableConfig = [
        {
          prop: '_action',
          name: 'Nome',
          actionName: 'titleCommon',
        },
        {
          prop: '_action',
          name: 'Respostas',
          actionName: 'answersCommon',
          width: 98,
        },
        {
          prop: '_action',
          name: 'Vídeos',
          actionName: 'videosCommon',
          width: 98,
          isHidden: this.isMobileBreakpoint,
        },
        {
          prop: '_action',
          name: '',
          actionName: 'statusCommon',
          width: 220,
          isHidden: this.isMobileBreakpoint,
        },
        {
          prop: '_action',
          name: '',
          actionName: 'actionCommon',
          width: 140,
          isHidden: this.isMobileBreakpoint,
        },
      ]
      return tableConfig
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('user', ['updateCreditsBalance']),
    checkIfSelected(row) {
      console.log('Checking if row is selected:', row)
      return false
    },
    handleEditAgent(agent) {
      if (agent.status === 'failed') {
        return
      }
      this.$router.push({ name: 'AgentPage', params: { id: agent.id } })
    },
    goToEmbedArticle() {
      console.log('Opening embed article:', this.embedArticle)
      window.open(this.embedArticle, '_blank')
    },
    updateTableHeight() {
      this.tableHeight = window.innerHeight
    },
    async refreshList() {
      this.fetching = true
      await this.getAssistants()
      this.fetching = false
    },
    getTagType(status) {
      const statusMap = {
        queued: 'neutral',
        processing: 'neutral',
        ready: 'success',
        disabled: 'neutral',
        failed: 'error',
        payment_failed: 'error',
      }
      return statusMap[status]
    },
    async toggleStatus(row) {
      try {
        const newStatus = row.status === 'disabled' ? 'ready' : 'disabled'
        const response = await pandaAssist.updateAssistantInfo(row.id, {
          status: newStatus,
        })
        if (response.status === 200) {
          row.status = newStatus
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            type: 'success',
            text: i18n.t(
              'message.pages.credits.creditsAutoRechargeModal.autoRechargeSuccess'
            ),
          })
          this.refreshList()
        } else {
          throw new Error('Falha ao atualizar o status')
        }
      } catch (error) {
        console.error('Erro ao alternar o status:', error)
      }
    },
    renameItem(row) {
      this.$root.$emit('videoModalRenameAssist', row.id)
    },
    async removeItem(row) {
      this.$root.$emit('deleteAssistModal', row.id, row.name)
    },
    openCredits() {
      this.$router.push({ name: 'Credits' }).catch(() => {})
    },
    initializeTooltips() {
      this.data = this.data.map((item) => ({
        ...item,
        showTooltip: true,
        hoverTooltip: false,
      }))

      setTimeout(() => {
        this.data = this.data.map((item) => ({
          ...item,
          showTooltip: false,
        }))
      }, 5000)
    },
    toggleHover(item, state) {
      if (!item.showTooltip) {
        this.$set(item, 'hoverTooltip', state)
      }
    },
    getStatusText(status) {
      const statusTextMap = {
        queued: 'message.pages.pandaAssist.converting',
        processing: 'message.pages.pandaAssist.converting',
        ready: 'message.pages.pandaAssist.ready',
        disabled: 'message.pages.pandaAssist.notReady',
        failed: 'message.error',
        payment_failed: 'message.pages.pandaAssist.error',
      }
      return this.$t(statusTextMap[status])
    },
    openMenu(item, event) {
      const rect = event.target.getBoundingClientRect()
      this.menuItem = item
      this.menuX = rect.left + window.scrollX
      this.menuY = rect.bottom + window.scrollY + 5

      this.menuVisible = true
      this.$nextTick(() => {
        const menuRef = this.$refs.menuRef
        if (menuRef) {
          menuRef.updateDimensions()
        }
      })
    },
    async getAssistants() {
      const response = await pandaAssist.getAssistants()
      this.data = response.data.filter((item) => item.status !== 'deleted')
      this.noCredits = this.data.some(
        (item) => item.status === 'payment_failed'
      )
      const videoIds = this.data
        .flatMap((item) => item.videos)
        .map((video) => video)

      this.pandaAssistVideos = videoIds
      this.fetching = false
    },
    openModal() {
      this.dialogVisible = true
      this.$root.$emit('pandaAssistVideos', this.pandaAssistVideos)
    },
    checkboxSelection(row) {
      console.log('Checkbox clicked for row:', row)
    },
  },
  watch: {
    '$vuetify.theme.dark'() {
      this.$nextTick(() => {
        const tableEl = this.$refs.virtualTable?.$el
        if (!tableEl) return       
        tableEl.style.display = 'none'
        tableEl.offsetHeight
        tableEl.style.display = ''
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/data-tableV2';
.rowIsSelected {
  background-color: #e0f7fa;
}
.rowIsDragged {
  opacity: 0.5;
}

.v-menu__content {
  position: absolute !important;
  z-index: 2000 !important;
}

.removeText {
  color: varFigma(feedback-danger);
}

.editButton {
  font-size: 12px !important;
  height: 24px !important;
  padding: 0 20px !important;
  border-radius: 4px !important;
}

::v-deep .panda-search:before {
  content: '\e90f';
  font-size: 16px;
}
::v-deep .item-line {
  border-top: 1px solid varFigma(neutral-95-lightest) !important;
}

::v-deep .item-cell {
  display: flex !important;
}

.nalps {
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border: 2px solid varFigma(neutral-80-lighter);
  border-radius: 50%;
  border-top: 2px solid varFigma(brand-60-base);
  -webkit-animation: spin 2s linear infinite;
  animation: spin 2s linear infinite;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}


</style>

<style lang="scss">
.t-header {
  display: none;
}

.data-table__content .virtual-scroller::-webkit-scrollbar {
  width: 4px;
  border-radius: 8px;
}

.data-table__content .virtual-scroller::-webkit-scrollbar-thumb {
  background-color: #d0d1d9 !important;
}

.data-table__content.virtualTable .virtual-scroller::-webkit-scrollbar-thumb {
  background-color: #52577a !important;
}
</style>
